'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { X } from '@/components/icons';
import { IconButton } from '@/components/ui/icon-button';
import { OverlayBusyProvider, useOverlayBusyState } from './overlay-busy';
import { OverlayFootContext } from './overlay-foot';
import styles from './dialog.module.css';
import { useT } from '@/lib/locale-context';

/**
 * Dialog - the settings/confirm dialog primitive: a native `<dialog>` opened
 * with `showModal()` (real backdrop, inert background, Esc-to-close), centered
 * on the page, with a title header + close, a scrollable body, and an optional
 * pinned footer for the action row. Document scroll is locked while open.
 *
 * Same substrate as `Sheet` (the two are siblings, not variants): a Sheet is
 * the thumb-reach surface for transient mobile choices; a Dialog is a seated
 * task with a Save. Same GEOMETRY as the Sheet too (since 2026-09-11): the
 * `<dialog>` is a transparent full-viewport frame sized `100dvh`, and the
 * visible panel is a flex child - centered on desktop, seated at the frame's
 * end on compact, so it lands at the VISIBLE bottom by construction (a
 * bottom-anchored top-layer dialog can sit behind a phone browser's
 * toolbar; the ui-kit rule). On compact it keeps its header and footer - it
 * is still a task, not a menu.
 *
 * This is NOT the route-intercepted item peek (`components/modal.tsx`), which
 * is bound to navigation (close = Back). A Dialog is plain controlled state:
 * `open` + `onClose`, no router involved.
 *
 * `busy` (ruled 2026-10-18): a seated task does not vanish mid-submit. While
 * a submit is in flight the X wears the kit's pending look (the
 * `IconButton`'s `pending`: disabled, `aria-busy`, the Spinner), the same cue
 * the busy submit wears, and Esc and the backdrop do nothing, so no close
 * path is dropped without a sign. The caller passes `busy`, or a form inside
 * the dialog reports its own flag with `useOverlayBusy` (overlay-busy.ts).
 */
export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  wide,
  top,
  busy: busyProp = false,
  testId,
}: {
  open: boolean;
  /** Called for every close path: Esc (cancel), backdrop click, the X. */
  onClose: () => void;
  title: string;
  /** Optional secondary line beneath the title (e.g. scope/context). */
  subtitle?: string;
  children: ReactNode;
  /** The pinned action row (Cancel / Save); omit for informational dialogs.
   *  Content in the body can seat its own act row there instead
   *  (`OverlayFoot`); the row hides while nothing fills it. */
  footer?: ReactNode;
  /** Working-column width (640px) instead of the 480px settings default. */
  wide?: boolean;
  /** Seat the panel near the top on desktop instead of centering it, for a
   *  body that grows while open (a search list): it grows downward, so the
   *  control under the pointer stays where it was. Compact is unchanged. */
  top?: boolean;
  /** The caller's submit is in flight: the X is busy, Esc and the backdrop
   *  do not close. Pass the same flag the submit button's `pending` reads; a
   *  form inside the body reports its own with `useOverlayBusy` instead. */
  busy?: boolean;
  testId?: string;
}) {
  const t = useT();
  const dialogRef = useRef<HTMLDialogElement>(null);
  // The foot element, published to the body so nested content can seat its
  // act row in it (`OverlayFoot`).
  const [foot, setFoot] = useState<HTMLElement | null>(null);
  const { busy, report } = useOverlayBusyState(open, busyProp);

  // Drive the native dialog from the `open` prop.
  useEffect(() => {
    const dlg = dialogRef.current;
    if (!dlg) return;
    if (open && !dlg.open) {
      dlg.showModal();
      // showModal's focus delegation lands on the first focusable descendant
      // (here: the Close button, or a field). A dialog opens to be READ, so
      // initial focus stays on the frame (tabIndex -1) - the Sheet's rule:
      // a field would open the on-screen keyboard WITH the panel and crush
      // it to a sliver (the New-view sheet on a phone, 2026-09-11), and a
      // button wears the platform's focus ring on a screen nobody tabbed
      // on. A descendant carrying the native `autofocus` attribute still
      // wins - the platform's opt-in. Tab walks into the fields.
      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        active !== dlg &&
        dlg.contains(active) &&
        !active.hasAttribute('autofocus')
      ) {
        dlg.focus();
      }
    } else if (!open && dlg.open) {
      dlg.close();
    }
  }, [open]);

  // Lock the document scroll while open (showModal inerts but does not lock).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      data-wide={wide || undefined}
      data-top={top || undefined}
      aria-label={title}
      data-testid={testId ?? 'dialog'}
      tabIndex={-1}
      onCancel={(e) => {
        // React synthesizes bubbling for `cancel`, so a NESTED dialog's Esc
        // reaches this handler too - act only on our own dialog's cancel.
        if (e.target !== dialogRef.current) return;
        // Esc: keep the parent's `open` state the source of truth. While
        // busy it closes nothing; the X's pending look says why.
        e.preventDefault();
        if (!busy) onClose();
      }}
      onClick={(e) => {
        // The dialog is the full-viewport FRAME (dialog.module.css): a click
        // anywhere outside the panel targets the dialog itself - dismiss.
        if (e.target === dialogRef.current && !busy) onClose();
      }}
    >
      <OverlayBusyProvider value={report}>
        <div className={styles.panel}>
          <header className={styles.head}>
            <div className={styles.titleBlock}>
              <h2 className={styles.title}>{title}</h2>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
            <IconButton
              label={t('kit.dialog.close')}
              onClick={onClose}
              pending={busy}
              data-testid="dialog-close"
            >
              <X aria-hidden />
            </IconButton>
          </header>
          <div className={styles.body}>
            <OverlayFootContext.Provider value={foot}>{children}</OverlayFootContext.Provider>
          </div>
          <footer className={styles.foot} ref={setFoot} data-overlay-foot>
            {footer}
          </footer>
        </div>
      </OverlayBusyProvider>
    </dialog>
  );
}
